/**
 * StorefrontOrderBanner — Unified storefront order card.
 *
 * Single card layout (tournament winner: V3 "Pill Legend Fieldset"):
 * - CTA groups with dashed fieldset borders + pill legend labels
 * - Customer info bar (name, avatar, payment status, total, order status)
 * - Order notes (if any)
 * - Expandable delivery/billing checklist
 * - Date row (order date + delivery date)
 * - Progress bars (delivery % + billing %)
 *
 * Returns null for non-storefront orders — zero impact on desk orders.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowUpRight,
  Banknote,
  Calendar,
  Check,
  ChevronDown,
  CreditCard,
  FileText,
  Loader2,
  MessageCircle,
  PackageCheck,
  Printer,
  Receipt,
  Truck,
  Undo2,
  Upload,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { ShipOrderDialog } from "./ShipOrderDialog";
import { RecordCODPaymentDialog } from "./RecordCODPaymentDialog";
import { RecordManualPaymentDialog } from "./RecordManualPaymentDialog";
import { RefundOrderDialog } from "./RefundOrderDialog";
import { useLinkedInvoice } from "../api/salesOrderHooks";
import { useCreateFulfillmentSession } from "@/features/fulfillment/api/fulfillmentHooks";
import { useStaffUploadSlip } from "@/features/storefront/payment-slips/hooks";
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/lib/store/authStore";
import { useQuery } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";

interface StorefrontOrderBannerProps {
  formData: Record<string, any>;
  salesOrderName: string;
}

/* ── Icons ────────────────────────────────────────────────── */

function StorefrontIcon({ className = "h-4 w-4 text-foreground" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  );
}

/* ── Status & payment derivation ─────────────────────────── */

function derivePaymentDot(status: string, paymentMethodType: string, advancePaid: number, grandTotal: number) {
  // Check actual money received first — advance_paid reflects submitted Payment Entries.
  // SO status only changes with Sales Invoice (per_billed), not Payment Entry (advance_paid).
  const fullyPaid = grandTotal > 0 && advancePaid >= grandTotal;

  if (status === "To Deliver and Bill") {
    if (fullyPaid) return { color: "bg-green-500", label: "ชำระแล้ว", textColor: "text-green-600 dark:text-green-400" };
    if (paymentMethodType === "COD") return null;
    if (advancePaid > 0) return { color: "bg-amber-500", label: "ชำระบางส่วน", textColor: "text-amber-600 dark:text-amber-400" };
    return { color: "bg-orange-500", label: "รอชำระ", textColor: "text-orange-600 dark:text-orange-400" };
  }
  if (status === "To Bill") {
    // "To Bill" = delivered but no SI yet. Check if money was actually received.
    if (fullyPaid) return { color: "bg-green-500", label: "ชำระแล้ว", textColor: "text-green-600 dark:text-green-400" };
    if (advancePaid > 0) return { color: "bg-amber-500", label: "ชำระบางส่วน", textColor: "text-amber-600 dark:text-amber-400" };
    return { color: "bg-amber-500", label: "รอรับเงิน", textColor: "text-amber-600 dark:text-amber-400" };
  }
  return { color: "bg-green-500", label: "ชำระแล้ว", textColor: "text-green-600 dark:text-green-400" };
}

function paymentMethodLabel(type: string): string {
  switch (type) {
    case "Bank Transfer": return "โอนเงิน";
    case "COD": return "เก็บเงินปลายทาง";
    case "Credit Card": return "บัตรเครดิต";
    default: return type || "ไม่ระบุ";
  }
}

function deriveStatusInfo(status: string, perDelivered: number, perBilled: number, advancePaid: number, grandTotal: number) {
  const s = (status || "").trim();
  if (s === "On Hold") return { text: "พักไว้ก่อน", color: "text-neutral-500", dotColor: "bg-neutral-400", pulse: false };
  if (s === "Cancelled") return { text: "ยกเลิกแล้ว", color: "text-red-600", dotColor: "bg-red-500", pulse: false };
  if (perDelivered >= 100 && perBilled >= 100) return { text: "เสร็จสิ้น", color: "text-green-600", dotColor: "bg-green-500", pulse: false };
  // Shopify-like: if delivered + money received, show "done" even if SI hasn't been auto-created yet
  const fullyPaid = grandTotal > 0 && advancePaid >= grandTotal;
  if (perDelivered >= 100 && fullyPaid && perBilled < 100) return { text: "ส่งของครบ · ชำระแล้ว", color: "text-green-600", dotColor: "bg-green-500", pulse: false };
  if (perDelivered >= 100 && perBilled < 100) return { text: "ส่งของครบ · รอเก็บเงิน", color: "text-amber-600", dotColor: "bg-amber-500", pulse: true };
  if (perBilled >= 100 && perDelivered < 100) return { text: "รับเงินครบ · รอส่งของ", color: "text-blue-600", dotColor: "bg-blue-500", pulse: true };
  if (fullyPaid && perDelivered < 100) return { text: "ชำระแล้ว · รอส่งของ", color: "text-blue-600", dotColor: "bg-blue-500", pulse: true };
  if (perDelivered > 0 || perBilled > 0) return { text: "กำลังดำเนินการ", color: "text-blue-600", dotColor: "bg-blue-500", pulse: true };
  return { text: "ออเดอร์ใหม่", color: "text-orange-600", dotColor: "bg-orange-500", pulse: true };
}

function formatCurrency(amount: number, curr: string) {
  const symbols: Record<string, string> = { THB: "฿", USD: "$", EUR: "€" };
  return `${symbols[curr] || curr}${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function deliveryLabelTh(pct: number): string {
  if (pct === 0) return "ยังไม่ได้ส่งของ";
  if (pct >= 100) return "ส่งของครบแล้ว";
  return "ส่งของแล้วบางส่วน";
}

function billingLabelTh(pct: number): string {
  if (pct === 0) return "ยังไม่ได้เก็บเงิน";
  if (pct >= 100) return "เก็บเงินครบแล้ว";
  return "เก็บเงินแล้วบางส่วน";
}

/* ── Checklist sub-component ─────────────────────────────── */

function ChecklistItem({ done, active, label }: { done: boolean; active: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className={cn(
        "flex h-5 w-5 items-center justify-center rounded-md shrink-0",
        done && "bg-green-500",
        active && !done && "border-2 border-blue-400 bg-blue-50",
        !done && !active && "border-2 border-border bg-card",
      )}>
        {done && <Check className="h-3 w-3 text-white" />}
      </div>
      <span className={cn(
        "text-[14px]",
        done && "text-neutral-500 line-through",
        active && !done && "text-neutral-800 font-medium",
        !done && !active && "text-neutral-400",
      )}>{label}</span>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────── */

export function StorefrontOrderBanner({
  formData,
  salesOrderName,
}: StorefrontOrderBannerProps) {
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [codDialogOpen, setCodDialogOpen] = useState(false);
  const [manualPaymentOpen, setManualPaymentOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [staffSlipDialogOpen, setStaffSlipDialogOpen] = useState(false);
  // Fulfillment/payment checklist: expanded by default (first view), but the
  // user's collapse/expand choice is remembered across orders via localStorage.
  const [checklistOpen, setChecklistOpen] = useState(() => {
    try {
      const stored = localStorage.getItem("so-fulfillment-checklist-open");
      return stored === null ? true : stored === "true";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("so-fulfillment-checklist-open", String(checklistOpen));
    } catch {
      /* localStorage unavailable — ignore */
    }
  }, [checklistOpen]);
  const navigate = useNavigate();

  const perBilled = Number(formData.per_billed) || 0;
  const isStorefront = formData.source_channel === "Storefront";
  const isSubmitted = formData.docstatus === 1;
  // Show action banner for all submitted orders (storefront, marketplace, admin/desk)
  const showBanner = isStorefront || isSubmitted;
  const { data: invoiceName } = useLinkedInvoice(
    salesOrderName,
    showBanner && perBilled > 0
  );
  const frappeUrl = useAuthStore((state) => state.frappeUrl);
  const createSession = useCreateFulfillmentSession();
  const { settings: tenantSettings } = useStorefrontTenantSettings();

  // Resolve the customer avatar (Customer.image, else LINE profile picture) via
  // the same server-side endpoint the orders list uses. Falls back to the icon.
  const customerLink = (formData.customer as string) || "";
  const { data: avatarMap } = useQuery({
    queryKey: ["customer-avatar-single", customerLink],
    queryFn: async () => {
      if (!customerLink) return {} as Record<string, string>;
      const map = await getERPClient().call<Record<string, string>>(
        "zaviago_backend.api.customer_list.get_customer_avatars",
        { customer_names: [customerLink] }
      );
      return map ?? {};
    },
    enabled: !!customerLink,
    staleTime: 60_000,
  });
  const customerImg = avatarMap?.[customerLink];

  if (!showBanner) return null;

  const status = (formData.status as string) || "";
  const paymentMethodType = (formData.payment_method_type as string) || "";
  const orderNotes = (formData.storefront_order_notes as string) || "";
  const vatRequested = formData.vat_invoice_requested === 1;

  // Refund state (Plan 1929) — custom fields set by admin_refund.issue_refund.
  const refundStatus = (formData.custom_refund_status as string) || "";
  const isRefunded = refundStatus === "Refunded";
  const isPartiallyRefunded = refundStatus === "Partially Refunded";

  const showSlipsLink = isStorefront && (paymentMethodType === "Bank Transfer" || paymentMethodType === "Event Checkout");
  const canStaffUploadSlip =
    showSlipsLink &&
    status === "To Deliver and Bill" &&
    !!tenantSettings?.allow_staff_manual_verification;

  const canShip =
    formData.docstatus === 1 &&
    !isRefunded &&
    (Number(formData.per_delivered) || 0) < 100 &&
    ["To Deliver and Bill", "To Deliver"].includes(status);

  const canRecordCODPayment =
    isStorefront && !isRefunded && paymentMethodType === "COD" && status === "To Bill";

  const canFulfillInWarehouse = canShip;

  const customerName = (formData.customer_name as string) || "";
  const grandTotal = Number(formData.grand_total) || 0;
  const currency = (formData.currency as string) || "";
  const perDelivered = Number(formData.per_delivered) || 0;
  const totalQty = Number(formData.total_qty) || 0;
  const transactionDate = (formData.transaction_date as string) || "";
  const deliveryDate = (formData.delivery_date as string) || "";
  const advancePaid = Number(formData.advance_paid) || 0;
  const isFullyPaidByAdvance = grandTotal > 0 && advancePaid >= grandTotal;

  const canRecordManualPayment =
    formData.docstatus === 1 &&
    !isRefunded &&
    ["To Deliver and Bill", "To Bill"].includes(status) &&
    !isFullyPaidByAdvance && // hide when fully paid via advance/PE
    !canRecordCODPayment; // COD has its own button

  // Refund is available once any payment has been received on a submitted order,
  // until it is fully refunded. The dialog re-checks exact eligibility server-side.
  const canRefund = isSubmitted && advancePaid > 0 && !isRefunded;

  const dot = derivePaymentDot(status, paymentMethodType, advancePaid, grandTotal);
  const statusInfo = deriveStatusInfo(status, perDelivered, perBilled, advancePaid, grandTotal);
  const hasActions =
    canShip || canRecordCODPayment || canRecordManualPayment || showSlipsLink ||
    canStaffUploadSlip || canRefund || isRefunded || isPartiallyRefunded;

  return (
    <>
      <div className="mb-4 rounded-xl border border-border bg-card overflow-clip">
        {/* CTA Groups — Pill Legend Fieldsets */}
        {hasActions && (
          <div className="flex flex-wrap items-stretch px-4 py-5 gap-3">
            {/* Group: Fulfillment */}
            {(canShip || canFulfillInWarehouse || canRecordCODPayment || canRecordManualPayment) && (
              <div className="relative flex items-center gap-2 rounded-xl border border-dashed border-blue-200 bg-blue-50/20 px-4 pt-5 pb-3.5 dark:border-blue-800 dark:bg-blue-950/20">
                <span className="absolute -top-2.5 left-3 text-[10px] font-bold text-blue-600 bg-blue-100 rounded-full px-2.5 py-0.5 dark:text-blue-400 dark:bg-blue-900">
                  จัดการออเดอร์
                </span>
                {canShip && (
                  <button
                    onClick={() => setShipDialogOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl bg-primary px-5 py-3 text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <Truck className="h-[18px] w-[18px]" />
                    <span className="text-[14px] font-semibold">ส่งของ</span>
                  </button>
                )}
                {canFulfillInWarehouse && (
                  <button
                    onClick={() => setWarehouseDialogOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <PackageCheck className="h-[18px] w-[18px] text-muted-foreground" />
                    <span className="text-[14px] font-medium text-foreground">Pick & Pack</span>
                  </button>
                )}
                {canRecordCODPayment && (
                  <button
                    onClick={() => setCodDialogOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <Banknote className="h-[18px] w-[18px] text-muted-foreground" />
                    <span className="text-[14px] font-semibold text-foreground">บันทึกการชำระ</span>
                  </button>
                )}
                {canRecordManualPayment && (
                  <button
                    onClick={() => setManualPaymentOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <CreditCard className="h-[18px] w-[18px] text-muted-foreground" />
                    <span className="text-[14px] font-semibold text-foreground">บันทึกการชำระ</span>
                  </button>
                )}
              </div>
            )}

            {/* Group: Documents */}
            {(showSlipsLink || canStaffUploadSlip) && (
              <div className="relative flex items-center gap-2 rounded-xl border border-dashed border-border bg-muted/30 px-4 pt-5 pb-3.5">
                <span className="absolute -top-2.5 left-3 text-[10px] font-bold text-muted-foreground bg-muted rounded-full px-2.5 py-0.5">
                  เอกสาร
                </span>
                {showSlipsLink && (
                  <Link
                    to={`/storefront/payment-slips?sales_order=${salesOrderName}`}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <FileText className="h-[18px] w-[18px] text-muted-foreground" />
                    <span className="text-[14px] font-medium text-foreground">สลิปชำระเงิน</span>
                    <ArrowUpRight className="h-3.5 w-3.5 text-neutral-400" />
                  </Link>
                )}
                {canStaffUploadSlip && (
                  <button
                    onClick={() => setStaffSlipDialogOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <Upload className="h-[18px] w-[18px] text-muted-foreground" />
                    <span className="text-[14px] font-medium text-foreground">อัปโหลดสลิป</span>
                  </button>
                )}
              </div>
            )}

            {/* Group: Finance / Refund */}
            {(canRefund || isRefunded || isPartiallyRefunded) && (
              <div className="relative flex items-center gap-2 rounded-xl border border-dashed border-rose-200 bg-rose-50/20 px-4 pt-5 pb-3.5 dark:border-rose-800 dark:bg-rose-950/20">
                <span className="absolute -top-2.5 left-3 text-[10px] font-bold text-rose-600 bg-rose-100 rounded-full px-2.5 py-0.5 dark:text-rose-400 dark:bg-rose-900">
                  การเงิน
                </span>
                {canRefund && (
                  <button
                    onClick={() => setRefundDialogOpen(true)}
                    className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:bg-muted"
                  >
                    <Undo2 className="h-[18px] w-[18px] text-rose-500" />
                    <span className="text-[14px] font-semibold text-foreground">คืนเงิน</span>
                  </button>
                )}
                {(isRefunded || isPartiallyRefunded) && (
                  <span className="flex items-center gap-2 rounded-xl bg-rose-100 px-4 py-3 text-[14px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                    <Undo2 className="h-[18px] w-[18px]" />
                    {isRefunded ? "คืนเงินแล้ว" : "คืนเงินบางส่วน"}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Customer Info Bar — storefront only */}
        {isStorefront && (
          <div className="px-6 py-4 border-t border-border bg-muted/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {customerImg ? (
                <img
                  src={customerImg}
                  alt=""
                  className="h-9 w-9 rounded-full object-cover bg-muted shrink-0"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="text-[16px] font-medium text-foreground">{customerName}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <StorefrontIcon className="h-3 w-3 text-primary" />
                  <span className="text-[12px] text-neutral-400">{paymentMethodLabel(paymentMethodType)}</span>
                  {dot && (
                    <>
                      <span className="text-neutral-300">·</span>
                      <span className={`inline-flex h-2 w-2 rounded-full ${dot.color}`} />
                      <span className={`text-[12px] ${dot.textColor}`}>{dot.label}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-[20px] font-bold text-foreground">{formatCurrency(grandTotal, currency)}</p>
                <p className="text-[12px] text-neutral-400">{totalQty} รายการ</p>
              </div>
              <button
                onClick={() => setChecklistOpen(!checklistOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  {statusInfo.pulse && <span className={`absolute inline-flex h-full w-full rounded-full ${statusInfo.dotColor} opacity-75 animate-ping`} />}
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${statusInfo.dotColor}`} />
                </span>
                <span className={`text-[14px] font-medium ${statusInfo.color}`}>{statusInfo.text}</span>
                <ChevronDown className={cn("h-3.5 w-3.5 text-neutral-400 transition-transform duration-200", checklistOpen && "rotate-180")} />
              </button>
            </div>
          </div>
        )}

        {/* Order notes + VAT + Invoice — storefront only */}
        {isStorefront && (orderNotes || vatRequested || invoiceName) && (
          <div className="px-6 py-2.5 border-t border-border bg-muted/50">
            <div className="flex flex-wrap items-center gap-4">
              {orderNotes && (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <MessageCircle className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <p className="text-[13px] text-neutral-500 truncate" title={orderNotes}>{orderNotes}</p>
                </div>
              )}
              {vatRequested && (
                <div className="flex items-center gap-2">
                  <Receipt className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <p className="text-[13px] text-neutral-500">ขอใบกำกับภาษี</p>
                </div>
              )}
              {invoiceName && (
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-neutral-400 shrink-0" />
                  <p className="text-[13px] text-foreground">{invoiceName}</p>
                  <button
                    onClick={() => {
                      const baseUrl = (frappeUrl || "").replace(/\/$/, "");
                      window.open(`${baseUrl}/printview?doctype=Sales%20Invoice&name=${encodeURIComponent(invoiceName)}`, "_blank");
                    }}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="พิมพ์ใบกำกับภาษี"
                  >
                    <Printer className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Expandable Checklist — storefront only */}
        {isStorefront && (
          <div className={`grid transition-all duration-300 ease-in-out ${checklistOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
            <div className="overflow-hidden">
              <div className="px-6 py-5 border-t border-border bg-muted/50">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Truck className="h-4 w-4 text-blue-500" />
                      <span className="text-[14px] font-medium text-foreground">ส่งของ</span>
                      <span className={cn("text-[13px] font-semibold ml-auto", perDelivered >= 100 && "text-green-600", perDelivered === 0 && "text-neutral-400", perDelivered > 0 && perDelivered < 100 && "text-blue-600")}>{deliveryLabelTh(perDelivered)}</span>
                    </div>
                    <div className="space-y-2">
                      <ChecklistItem done={perDelivered > 0} active={perDelivered === 0} label="รับออเดอร์" />
                      <ChecklistItem done={perDelivered >= 50} active={perDelivered > 0 && perDelivered < 100} label="จัดเตรียมสินค้า" />
                      <ChecklistItem done={perDelivered >= 100} active={false} label="ส่งของให้ลูกค้า" />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <CreditCardIcon className="h-4 w-4 text-emerald-500" />
                      <span className="text-[14px] font-medium text-foreground">เก็บเงิน</span>
                      <span className={cn("text-[13px] font-semibold ml-auto", (perBilled >= 100 || isFullyPaidByAdvance) && "text-green-600", perBilled === 0 && !isFullyPaidByAdvance && "text-neutral-400", perBilled > 0 && perBilled < 100 && !isFullyPaidByAdvance && "text-emerald-600")}>{isFullyPaidByAdvance && perBilled < 100 ? "ชำระแล้ว" : billingLabelTh(perBilled)}</span>
                    </div>
                    <div className="space-y-2">
                      <ChecklistItem done={perBilled > 0 || isFullyPaidByAdvance} active={perBilled === 0 && !isFullyPaidByAdvance} label="ออกบิล / แจ้งยอด" />
                      <ChecklistItem done={perBilled >= 50 || isFullyPaidByAdvance} active={perBilled > 0 && perBilled < 100 && !isFullyPaidByAdvance} label="รับชำระเงิน" />
                      <ChecklistItem done={perBilled >= 100 || isFullyPaidByAdvance} active={false} label="เก็บเงินครบ" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date Row — storefront only */}
        {isStorefront && (
          <div className="grid grid-cols-2 divide-x divide-border border-t border-border bg-muted/50">
            <div className="px-6 py-3 flex items-center gap-2.5">
              <Calendar className="h-4 w-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-400">สั่งเมื่อ</span>
              <span className="text-[14px] text-foreground">{transactionDate}</span>
            </div>
            <div className="px-6 py-3 flex items-center gap-2.5">
              <Truck className="h-4 w-4 text-neutral-400" />
              <span className="text-[12px] text-neutral-400">กำหนดส่ง</span>
              <span className="text-[14px] text-foreground">{deliveryDate}</span>
            </div>
          </div>
        )}

        {/* Progress Bars — storefront only */}
        {isStorefront && (
          <div className="px-6 py-3 border-t border-border">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2.5 flex-1">
                <span className="text-[13px] text-neutral-400 shrink-0">ส่งของ</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${perDelivered >= 100 ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${Math.min(perDelivered, 100)}%` }} />
                </div>
                <span className="text-[13px] font-medium text-neutral-500 w-10 text-right">{Math.round(perDelivered)}%</span>
              </div>
              <div className="w-px h-5 bg-muted" />
              <div className="flex items-center gap-2.5 flex-1">
                <span className="text-[13px] text-neutral-400 shrink-0">เก็บเงิน</span>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${(perBilled >= 100 || isFullyPaidByAdvance) ? "bg-green-500" : "bg-emerald-500"}`} style={{ width: `${isFullyPaidByAdvance ? 100 : Math.min(perBilled, 100)}%` }} />
                </div>
                <span className="text-[13px] font-medium text-neutral-500 w-10 text-right">{isFullyPaidByAdvance ? "100%" : `${Math.round(perBilled)}%`}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs — unchanged */}
      <ShipOrderDialog
        open={shipDialogOpen}
        onOpenChange={setShipDialogOpen}
        salesOrderName={salesOrderName}
        customerName={customerName}
        items={Array.isArray(formData.items) ? formData.items : []}
      />
      <RecordCODPaymentDialog
        open={codDialogOpen}
        onOpenChange={setCodDialogOpen}
        salesOrderName={salesOrderName}
        grandTotal={grandTotal}
        currency={currency}
      />
      <RecordManualPaymentDialog
        open={manualPaymentOpen}
        onOpenChange={setManualPaymentOpen}
        salesOrderName={salesOrderName}
        grandTotal={grandTotal}
        currency={currency}
      />
      <RefundOrderDialog
        open={refundDialogOpen}
        onOpenChange={setRefundDialogOpen}
        salesOrderName={salesOrderName}
        currency={currency}
      />

      {canStaffUploadSlip && (
        <StaffSlipUploadDialog
          open={staffSlipDialogOpen}
          onOpenChange={setStaffSlipDialogOpen}
          salesOrderName={salesOrderName}
        />
      )}

      <AlertDialog
        open={warehouseDialogOpen}
        onOpenChange={setWarehouseDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start Warehouse Fulfillment</AlertDialogTitle>
            <AlertDialogDescription>
              This will start a warehouse fulfillment session for this order.
              You'll be redirected to the pick walk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={createSession.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                createSession.mutate(
                  { salesOrders: [salesOrderName] },
                  {
                    onSuccess: (result) => {
                      navigate(`/fulfillment/pick-list/${result.session_name}`);
                    },
                    onError: () => {
                      setWarehouseDialogOpen(false);
                    },
                  }
                );
              }}
              disabled={createSession.isPending}
            >
              {createSession.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  Creating...
                </>
              ) : (
                "Continue"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/* ── Staff Slip Upload Dialog ────────────────────────────── */

function StaffSlipUploadDialog({
  open,
  onOpenChange,
  salesOrderName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderName: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const uploadMutation = useStaffUploadSlip();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const result = await uploadMutation.mutateAsync({
      salesOrder: salesOrderName,
      file: selectedFile,
      note: note || undefined,
    });
    if (result.payment_entry_warning) {
      toast.warning(result.payment_entry_warning);
    } else {
      toast.success("Payment slip verified successfully.");
    }
    setSelectedFile(null);
    setPreview(null);
    setNote("");
    onOpenChange(false);
  };

  const handleClose = (v: boolean) => {
    if (!v) {
      setSelectedFile(null);
      setPreview(null);
      setNote("");
    }
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Payment Slip</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload a payment slip for {salesOrderName}. This will mark the
            payment as verified without automatic validation.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            onChange={handleFileChange}
            className="hidden"
          />

          {preview ? (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full overflow-hidden rounded-sm border"
            >
              <img
                src={preview}
                alt="Slip preview"
                className="w-full object-contain max-h-48"
              />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full flex-col items-center justify-center gap-2 rounded-sm border-2 border-dashed p-8 text-muted-foreground hover:border-muted-foreground/40 hover:bg-muted/30 transition-colors"
            >
              <Upload className="h-8 w-8" />
              <span className="text-sm">Click to select slip image</span>
            </button>
          )}

          <Input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <div className="flex gap-2">
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isPending}
              className="flex-1"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                "Upload & Verify"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleClose(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>

          {uploadMutation.isError && (
            <p className="text-sm text-red-600">
              {uploadMutation.error?.message || "Upload failed"}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
